import { useCallback, useEffect } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { serverErrorMessage } from "../lib/apiError";
import { getCsrfHeader } from "../lib/csrf";
import { isActive } from "../lib/announcements";
import { useCachedQuery, type CachedQueryStatus } from "./useCachedQuery";

// Mirrors AnnouncementModel.kt. An announcement is written, dry-run on every
// instance (where would it land?), then sent. ztc-core's dispatcher does the
// box work, so every POST returns at once and the detail view polls.

export const MAX_ANNOUNCEMENT_LENGTH = 1500;

export type AnnouncementState = "draft" | "sending" | "sent" | "cancelled";

export type PreviewStatus = "pending" | "would_deliver" | "unreachable" | "failed" | "plugin_missing" | "skipped";

export type DeliveryStatus =
  | "not_sent"
  | "queued"
  | "sending"
  | "delivered"
  | "failed"
  | "unreachable"
  | "unknown"
  | "plugin_missing"
  | "skipped"
  | "cancelled";

export type AnnouncementSummary = {
  id: string;
  body: string;
  audience: "all" | "selected";
  createdBy: string;
  createdAt: string;
  sentBy: string | null;
  sentAt: string | null;
  cancelledAt: string | null;
  state: AnnouncementState;
  total: number;
  counts: Partial<Record<DeliveryStatus, number>>;
  previewCounts: Partial<Record<PreviewStatus, number>>;
};

export type AnnouncementDelivery = {
  tenantId: string;
  tenantName: string;
  ownerEmail: string;
  previewStatus: PreviewStatus;
  previewChannel: string | null;
  previewTarget: string | null;
  previewSource: string | null;
  previewReason: string | null;
  status: DeliveryStatus;
  channel: string | null;
  target: string | null;
  source: string | null;
  reason: string | null;
  timedOut: boolean;
  agentAware: boolean | null;
  attempts: number;
  inFlight: boolean;
  startedAt: string | null;
  finishedAt: string | null;
};

export type AnnouncementDetail = {
  announcement: AnnouncementSummary;
  deliveries: ReadonlyArray<AnnouncementDelivery>;
};

export type AnnouncementResult<T> = { ok: true; value: T } | { ok: false; message: string };

const LIST_URL = ADMIN_QUERIES.announcements.url;
const detailUrl = (id: string): string => `${LIST_URL}/${encodeURIComponent(id)}`;

// A dry run or a send is seconds per instance on a worker pool of four, so
// while one is moving the detail view refreshes quickly; once everything has
// settled it falls back to the standard dwell interval.
const ACTIVE_POLL_MS = 3_000;
const IDLE_POLL_MS = 30_000;

const post = async <T>(url: string, body: unknown, fallback: string): Promise<AnnouncementResult<T>> => {
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...getCsrfHeader() },
      body: body === undefined ? null : JSON.stringify(body),
    });
    if (!res.ok) return { ok: false, message: await serverErrorMessage(res, fallback) };
    return { ok: true, value: (await res.json()) as T };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : fallback };
  }
};

export type UseAdminAnnouncementsResult = {
  status: CachedQueryStatus;
  announcements: ReadonlyArray<AnnouncementSummary>;
  error: string | null;
  isFetching: boolean;
  create: (body: string, tenantIds: ReadonlyArray<string> | null) => Promise<AnnouncementResult<AnnouncementDetail>>;
};

export const useAdminAnnouncements = (): UseAdminAnnouncementsResult => {
  const query = useCachedQuery<ReadonlyArray<AnnouncementSummary>>({
    cacheResource: ADMIN_QUERIES.announcements.resource,
    queryKey: LIST_URL,
    fetcher: async (signal) => {
      const res = await fetch(LIST_URL, { credentials: "include", signal });
      if (!res.ok) throw new Error(`Failed to load announcements (${res.status})`);
      return (await res.json()) as ReadonlyArray<AnnouncementSummary>;
    },
  });
  const { refetch } = query;

  const create = useCallback(
    async (body: string, tenantIds: ReadonlyArray<string> | null): Promise<AnnouncementResult<AnnouncementDetail>> => {
      const result = await post<AnnouncementDetail>(LIST_URL, { body, tenantIds }, "Could not create the announcement");
      if (result.ok) void refetch();
      return result;
    },
    [refetch],
  );

  return {
    status: query.status,
    announcements: query.data ?? [],
    error: query.error,
    isFetching: query.isFetching,
    create,
  };
};

export type UseAdminAnnouncementResult = {
  status: CachedQueryStatus;
  detail: AnnouncementDetail | null;
  error: string | null;
  isFetching: boolean;
  send: () => Promise<AnnouncementResult<AnnouncementDetail>>;
  retry: () => Promise<AnnouncementResult<AnnouncementDetail>>;
  cancel: () => Promise<AnnouncementResult<AnnouncementDetail>>;
};

export const useAdminAnnouncement = (id: string): UseAdminAnnouncementResult => {
  const query = useCachedQuery<AnnouncementDetail>({
    // Network-only: keyed by announcement, and the rows move while it sends.
    cacheResource: null,
    queryKey: detailUrl(id),
    fetcher: async (signal) => {
      const res = await fetch(detailUrl(id), { credentials: "include", signal });
      if (!res.ok) throw new Error(await serverErrorMessage(res, "Failed to load the announcement"));
      return (await res.json()) as AnnouncementDetail;
    },
    refreshIntervalMs: IDLE_POLL_MS,
  });
  const { refetch, mutate } = query;
  const active = isActive(query.data);

  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => {
      void refetch();
    }, ACTIVE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [active, refetch]);

  const act = useCallback(
    async (action: "send" | "retry" | "cancel", fallback: string): Promise<AnnouncementResult<AnnouncementDetail>> => {
      const result = await post<AnnouncementDetail>(`${detailUrl(id)}/${action}`, undefined, fallback);
      if (result.ok) mutate(() => result.value);
      return result;
    },
    [id, mutate],
  );

  return {
    status: query.status,
    detail: query.data,
    error: query.error,
    isFetching: query.isFetching,
    send: useCallback(() => act("send", "Could not send the announcement"), [act]),
    retry: useCallback(() => act("retry", "Could not retry"), [act]),
    cancel: useCallback(() => act("cancel", "Could not cancel"), [act]),
  };
};
